import { useMemo } from "react";

import type { TaskType } from "@/types/content";

import type { FormValues } from "../types";
import { buildClusters, getFieldPresets } from "../utils";
import { DynamicField } from "./DynamicField";
import { FormCard } from "./FormCard";
import { OneOfField } from "./OneOfField";
import { PresetClusterField } from "./PresetClusterField";

interface Props {
  taskType: TaskType;
  values: FormValues;
  setValues: (patch: FormValues) => void;
}

export const DynamicForm = ({ taskType, values, setValues }: Props) => {
  const fieldById = useMemo(
    () => new Map(taskType.fields.map((f) => [f.id, f])),
    [taskType.fields],
  );

  const clusters = useMemo(() => buildClusters(taskType.fields), [taskType.fields]);

  const renderField = (fieldId: string) => {
    const cluster = clusters.get(fieldId);
    if (cluster) {
      if (cluster[0].id !== fieldId) return null;
      const hub = cluster.find((f) => getFieldPresets(f).length > 0);
      if (hub) {
        return (
          <PresetClusterField
            key={cluster[0].id}
            cluster={cluster}
            hub={hub}
            values={values}
            setValues={setValues}
          />
        );
      }
      return (
        <OneOfField
          key={cluster[0].id}
          cluster={cluster}
          values={values}
          setValues={setValues}
        />
      );
    }

    const field = fieldById.get(fieldId);
    if (!field) return null;
    return (
      <DynamicField
        key={field.id}
        field={field}
        value={values[field.id]}
        onChange={(v) => setValues({ [field.id]: v })}
      />
    );
  };

  const groupedIds = new Set(taskType.groups.flatMap((g) => g.fields.map((f) => f.id)));
  const ungrouped = taskType.fields.filter((f) => !groupedIds.has(f.id));

  return (
    <div className="space-y-5 sm:space-y-8">
      {taskType.groups.map((group, i) => (
        <FormCard key={group.title || i} className="space-y-4">
          {group.fields.map((f) => renderField(f.id))}
        </FormCard>
      ))}

      {ungrouped.length > 0 && (
        <FormCard className="space-y-4">
          {ungrouped.map((f) => renderField(f.id))}
        </FormCard>
      )}
    </div>
  );
};
